import { NavLink, useNavigate } from "react-router-dom";
import { logout, useAuth } from "../../lib/auth.js";
import { Icon, type IconName } from "../ui/Icon.js";

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  ai?: boolean;
  badge?: "PRO";
  count?: number;
}

const TRADE_ITEMS: NavItem[] = [
  { to: "/", label: "Dashboard", icon: "chart" },
  { to: "/portfolio", label: "Portfolio", icon: "pie" },
  { to: "/signals", label: "AI Signals", icon: "sparkle", ai: true, count: 7 },
  { to: "/scanner", label: "Scanner", icon: "radar" },
  { to: "/leaderboard", label: "Leaderboard", icon: "trophy" },
  { to: "/paper", label: "Paper Trading", icon: "flask" },
];

const INTEL_ITEMS: NavItem[] = [
  { to: "/training", label: "AI Training", icon: "cpu", ai: true, badge: "PRO" },
  { to: "/dca", label: "DCA Automation", icon: "repeat", badge: "PRO" },
  { to: "/alerts", label: "Price Alerts", icon: "bell" },
  { to: "/health", label: "System Health", icon: "activity" },
];

const FOOT_ITEMS: NavItem[] = [
  { to: "/keys", label: "Exchange Keys", icon: "key" },
  { to: "/settings", label: "Settings", icon: "gear" },
];

function NavRow({ item }: { item: NavItem }) {
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      className={({ isActive }) =>
        `se-nav-item ${item.ai ? "ai-item" : ""} ${isActive ? "active" : ""}`
      }
    >
      <span className="se-nav-icon">
        <Icon name={item.icon} size={15} />
      </span>
      <span>{item.label}</span>
      {item.count !== undefined ? <span className="se-nav-count">{item.count}</span> : null}
      {item.badge ? <span className="se-nav-badge pro">{item.badge}</span> : null}
    </NavLink>
  );
}

interface SidebarProps {
  plan?: string;
}

function initialsFor(email: string | undefined): string {
  if (!email) return "OP";
  const local = email.split("@")[0] ?? "";
  const parts = local.split(/[._-]/).filter(Boolean);
  if (parts.length >= 2)
    return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return (local.slice(0, 2) || "OP").toUpperCase();
}

export function Sidebar({ plan = "PRO" }: SidebarProps) {
  const user = useAuth((s) => s.user);
  const navigate = useNavigate();
  const userName = user?.email ?? "Operator";
  const userInitials = initialsFor(user?.email);

  function onLogout(): void {
    logout();
    navigate("/signin", { replace: true });
  }

  return (
    <aside className="se-sidebar">
      <div className="se-logo">
        <div className="se-logo-mark">
          <span>S</span>
        </div>
        <div className="se-logo-text">SpotEdge</div>
      </div>
      <nav className="se-nav">
        <div className="se-nav-section-label">Trade</div>
        {TRADE_ITEMS.map((it) => (
          <NavRow key={it.to} item={it} />
        ))}
        <div className="se-nav-section-label">Intelligence</div>
        {INTEL_ITEMS.map((it) => (
          <NavRow key={it.to} item={it} />
        ))}
      </nav>
      <div className="se-sidebar-foot">
        {FOOT_ITEMS.map((it) => (
          <NavRow key={it.to} item={it} />
        ))}
        <div className="se-user-card" style={{ marginTop: 4 }} onClick={onLogout} role="button" title="Sign out">
          <div className="se-avatar">{userInitials}</div>
          <div className="se-user-meta">
            <div
              className="se-user-name"
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {userName}
            </div>
            <div className="se-user-plan">
              <span className="se-plan-pill">{plan}</span>
              <span style={{ marginLeft: 6, color: "var(--text-faint)", fontSize: 10 }}>
                Sign out
              </span>
            </div>
          </div>
          <Icon name="chevronUp" size={14} className="muted" />
        </div>
      </div>
    </aside>
  );
}
